/**
 * Cost Estimation Service
 * Handles API calls for product cost estimation, bar stock prices, and cost settings.
 */
import httpClient from './httpClient';

// ==================== Bar Stock Prices ====================

/**
 * Get list of bar stock prices with pagination and filtering.
 * @param {Object} options - Query options
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.pageSize=50] - Items per page
 * @param {number} [options.diameterMin] - Minimum diameter filter
 * @param {number} [options.diameterMax] - Maximum diameter filter
 * @param {boolean} [options.isActive] - Filter by active status
 * @param {string} [options.supplierName] - Filter by supplier name
 */
export const getBarStockPrices = async (options = {}) => {
  const {
    page = 1,
    pageSize = 50,
    diameterMin,
    diameterMax,
    isActive,
    supplierName
  } = options;

  const params = {
    page,
    page_size: pageSize
  };

  if (diameterMin !== undefined) params.diameter_min = diameterMin;
  if (diameterMax !== undefined) params.diameter_max = diameterMax;
  if (isActive !== undefined) params.is_active = isActive;
  if (supplierName) params.supplier_name = supplierName;

  const response = await httpClient.get('/api/bar-stock-prices', { params });
  return response.data;
};

/**
 * Get a single bar stock price by ID.
 * @param {string} id - Bar stock price ID
 */
export const getBarStockPrice = async (id) => {
  const response = await httpClient.get(`/api/bar-stock-prices/${id}`);
  return response.data;
};

/**
 * Look up bar stock price for a given tip diameter.
 * Rounds up to nearest 0.5mm specification.
 * @param {number} diameter - Product tip diameter in mm
 */
export const lookupBarStock = async (diameter) => {
  const response = await httpClient.get('/api/bar-stock-prices/lookup', {
    params: { diameter }
  });
  return response.data;
};

/**
 * Create a new bar stock price entry.
 * Admin only.
 * @param {Object} data - Bar stock price data
 * @param {number} data.diameter - Diameter in mm
 * @param {number} [data.length=310] - Length in mm
 * @param {number} data.unit_price_tax_included - Tax-included price
 * @param {number} [data.tax_rate=0.13] - Tax rate
 * @param {string} [data.supplier_name] - Supplier name
 * @param {string} [data.brand] - Brand/grade
 * @param {number} [data.delivery_days] - Delivery days
 * @param {string} [data.notes] - Notes
 */
export const createBarStockPrice = async (data) => {
  const response = await httpClient.post('/api/bar-stock-prices', data);
  return response.data;
};

/**
 * Update a bar stock price entry.
 * Admin only.
 * @param {string} id - Bar stock price ID
 * @param {Object} data - Updated data
 */
export const updateBarStockPrice = async (id, data) => {
  const response = await httpClient.put(`/api/bar-stock-prices/${id}`, data);
  return response.data;
};

/**
 * Delete (soft delete) a bar stock price entry.
 * Admin only.
 * @param {string} id - Bar stock price ID
 */
export const deleteBarStockPrice = async (id) => {
  const response = await httpClient.delete(`/api/bar-stock-prices/${id}`);
  return response.data;
};

/**
 * Bulk import bar stock prices.
 * Admin only.
 * @param {Object[]} items - Array of bar stock price objects
 * @param {boolean} [replace=false] - Replace existing entries
 */
export const bulkImportBarStockPrices = async (items, replace = false) => {
  const response = await httpClient.post('/api/bar-stock-prices/bulk-import', {
    items,
    replace
  });
  return response.data;
};

// ==================== Cost Settings ====================

/**
 * Get all active cost settings.
 */
export const getCostSettings = async () => {
  const response = await httpClient.get('/api/cost-settings');
  return response.data;
};

/**
 * Get a specific cost setting by type.
 * @param {string} settingType - Setting type: 'labor_costs' | 'electricity' | 'work_hours'
 */
export const getCostSetting = async (settingType) => {
  const response = await httpClient.get(`/api/cost-settings/${settingType}`);
  return response.data;
};

/**
 * Update a cost setting.
 * Admin only.
 * @param {string} settingType - Setting type
 * @param {Object} settings - New settings values
 */
export const updateCostSetting = async (settingType, settings) => {
  const response = await httpClient.put(`/api/cost-settings/${settingType}`, {
    settings
  });
  return response.data;
};

/**
 * Get work hours categories configuration.
 */
export const getWorkHoursCategories = async () => {
  const response = await httpClient.get('/api/cost-settings/work-hours/categories');
  return response.data;
};

/**
 * Add or update a category's work hours.
 * Admin only.
 * @param {string} category - Category name
 * @param {number} hours - Work hours
 */
export const updateWorkHoursCategory = async (category, hours) => {
  const response = await httpClient.put('/api/cost-settings/work-hours/categories', {
    category,
    hours
  });
  return response.data;
};

/**
 * Remove a category's work hours configuration.
 * Admin only.
 * @param {string} category - Category name
 */
export const deleteWorkHoursCategory = async (category) => {
  const response = await httpClient.delete(`/api/cost-settings/work-hours/categories/${encodeURIComponent(category)}`);
  return response.data;
};

// ==================== Cost Estimation ====================

/**
 * Calculate manufacturing cost for a product.
 * @param {Object} params - Calculation parameters
 * @param {string} [params.product_id] - Product ID (option 1)
 * @param {number} [params.tip_diameter] - Tip diameter in mm (option 2)
 * @param {number} [params.total_length] - Total length in mm (option 2)
 * @param {string} [params.category] - Product category
 * @param {string} [params.material] - Material type
 * @param {boolean} [params.save_history=false] - Save to history
 */
export const calculateCost = async (params) => {
  const response = await httpClient.post('/api/cost-estimation/calculate', params);
  return response.data;
};

/**
 * Calculate cost for a product by its ID.
 * @param {string} productId - Product ID
 * @param {boolean} [saveHistory=false] - Save to history
 */
export const calculateCostByProductId = async (productId, saveHistory = false) => {
  return calculateCost({
    product_id: productId,
    save_history: saveHistory
  });
};

/**
 * Calculate costs for multiple products.
 * @param {string[]} productIds - Array of product IDs
 * @param {boolean} [saveHistory=false] - Save to history
 */
export const batchCalculateCost = async (productIds, saveHistory = false) => {
  const response = await httpClient.post('/api/cost-estimation/batch', {
    product_ids: productIds,
    save_history: saveHistory
  });
  return response.data;
};

/**
 * Preview cost estimation without saving.
 * @param {number} tipDiameter - Tip diameter in mm
 * @param {number} totalLength - Total length in mm
 * @param {string} [category] - Product category
 * @param {string} [material] - Material type
 */
export const previewCostEstimation = async (tipDiameter, totalLength, category, material) => {
  const response = await httpClient.post('/api/cost-estimation/preview', {
    tip_diameter: tipDiameter,
    total_length: totalLength,
    category,
    material
  });
  return response.data;
};

/**
 * Validate cost estimation parameters.
 * @param {number} tipDiameter - Tip diameter in mm
 * @param {number} totalLength - Total length in mm
 */
export const validateCostParams = async (tipDiameter, totalLength) => {
  const response = await httpClient.post('/api/cost-estimation/validate-params', {
    tip_diameter: tipDiameter,
    total_length: totalLength
  });
  return response.data;
};

/**
 * Get cost estimation history.
 * @param {Object} options - Query options
 * @param {string} [options.productId] - Filter by product ID
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.pageSize=20] - Items per page
 */
export const getEstimationHistory = async (options = {}) => {
  const { productId, page = 1, pageSize = 20 } = options;
  const params = { page, page_size: pageSize };
  if (productId) params.product_id = productId;

  const response = await httpClient.get('/api/cost-estimation/history', { params });
  return response.data;
};

/**
 * Get cost estimation history for a specific product.
 * @param {string} productId - Product ID
 * @param {number} [limit=10] - Maximum records to return
 */
export const getProductEstimationHistory = async (productId, limit = 10) => {
  const response = await httpClient.get(`/api/cost-estimation/history/${productId}`, {
    params: { limit }
  });
  return response.data;
};

/**
 * Look up matching bar stock for a tip diameter.
 * @param {number} diameter - Tip diameter in mm
 */
export const lookupBarStockForEstimation = async (diameter) => {
  const response = await httpClient.get('/api/cost-estimation/lookup-bar-stock', {
    params: { diameter }
  });
  return response.data;
};

// ==================== Utility Functions ====================

/**
 * Format currency value.
 * @param {number} value - Value to format
 * @param {string} [currency='CNY'] - Currency code
 */
export const formatCurrency = (value, currency = 'CNY') => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

/**
 * Format percentage value.
 * @param {number} value - Value (0-1 or 0-100)
 */
export const formatPercentage = (value) => {
  if (value === null || value === undefined) return '-';
  // Assume value > 1 is already in percentage format
  const pct = value > 1 ? value : value * 100;
  return `${pct.toFixed(1)}%`;
};

/**
 * Calculate margin price from cost and margin percentage.
 * @param {number} cost - Base cost
 * @param {number} marginPercent - Margin percentage (e.g., 20 for 20%)
 */
export const calculateMarginPrice = (cost, marginPercent) => {
  if (!cost || !marginPercent) return null;
  return cost * (1 + marginPercent / 100);
};

// Default export with all functions
const costEstimationService = {
  // Bar Stock Prices
  getBarStockPrices,
  getBarStockPrice,
  lookupBarStock,
  createBarStockPrice,
  updateBarStockPrice,
  deleteBarStockPrice,
  bulkImportBarStockPrices,

  // Cost Settings
  getCostSettings,
  getCostSetting,
  updateCostSetting,
  getWorkHoursCategories,
  updateWorkHoursCategory,
  deleteWorkHoursCategory,

  // Cost Estimation
  calculateCost,
  calculateCostByProductId,
  batchCalculateCost,
  previewCostEstimation,
  validateCostParams,
  getEstimationHistory,
  getProductEstimationHistory,
  lookupBarStockForEstimation,

  // Utilities
  formatCurrency,
  formatPercentage,
  calculateMarginPrice
};

export default costEstimationService;
